/**
 * Cover Configurator — Pricing Engine + Interactive SVG Preview (v1)
 * uscushion.com · 2026-07
 *
 * A "cover" is fabric-only (no foam): a replacement slipcover for an existing
 * cushion. Price = COVER_FACTOR (0.7) × the standard cushion formula, mirroring
 * pricing.js (calcFinalPrice with formulaType === 'cover'). The apiPayload sends
 * formulaType: 'cover' so the Worker applies the same 0.7 factor server-side.
 *
 * v1 scope: rectangular single-piece only (shape section auto-set + hidden).
 * SVG orientation: Width = horizontal front edge, Length = depth (diagonal).
 */
(function () {
  'use strict';

  // Capture the real page title at load. Chat widgets (tawk.to / Chaty) mutate
  // document.title to "1 new message" when a message pops; if a customer adds to
  // cart at that instant, _Source Product would record the chat noise instead of
  // the product name — blurring cover vs bench in order details. At script-load
  // time the title is still clean, so we freeze it here.
  var INITIAL_TITLE = document.title;

  // Robust source-product name: frozen title first, og:title (server-rendered,
  // chat can't touch it) as fallback, URL path as last resort.
  function sourceProductName() {
    var clean = function (s) { return (s || '').split('–')[0].split('—')[0].trim(); };
    var noise = /new message|nuevo mensaje|unread|\(\d+\)/i;
    var t = clean(INITIAL_TITLE);
    if (!t || noise.test(t)) {
      var og = document.querySelector('meta[property="og:title"]');
      t = clean(og && og.content);
    }
    return (t && !noise.test(t)) ? t : location.pathname;
  }

  /* ─── Cost constants ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MIN_PRICE         = 45;
  var COVER_FACTOR      = 0.7;  // cover = fabric-only, 70% of cushion price

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    shape:       { rectangular: 0, tufted_rectangle: 0, france_tufted: 0, custom_shape: 50 },
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    extraCovers: { '0': 0, '1': 30, '2': 50 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    rectangular: 'Rectangular', tufted_rectangle: 'Tufted cushion',
    france_tufted: 'France tufted cushion', custom_shape: 'Custom shape',
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    shape: '', L: 0, W: 0, T: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing ─── */
  function calcBasePrice(L, W, T, ec) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    if (ec > 0) totalWeight += fabricArea * FABRIC_GSM / 1000 * ec;
    var totalRMB = fabricArea * FABRIC_PRICE_RMB + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / TARGET_COST_RATIO * COVER_FACTOR, MIN_PRICE);
  }

  function getAddonTotal() {
    return (ADDON.shape[state.shape] || 0) + (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + (ADDON.extraCovers[state.extraCovers] || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0) + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function isComplete() {
    return state.shape && state.L >= 6 && state.W >= 6 && state.T >= 1
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — Width = horizontal front edge, Length = depth
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var L = state.L, W = state.W, T = state.T;
    var empty = (!L && !W && !T);
    // Per-dimension fallback (not just the all-empty case): while the customer is
    // still typing — e.g. Length entered but Width/Thickness blank — a 0 width/
    // thickness would make uT/uW = 0/0 = NaN and poison every SVG coordinate
    // (<polygon>/<line>/<text> "NaN" console errors). Keep each unit positive.
    var uL = L > 0 ? L : 2.5;
    var uW = W > 0 ? W : 1;
    var uT = T > 0 ? T : 0.12;

    /* ── Geometry: Length = long front-right edge, Width = short back-left diagonal ── */
    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }

    var ratio = clamp(uL / uW, 1, 6);
    var fWidthPx = clamp(300 / (1 + ratio * 0.5), 60, 170);
    var fLenPx   = clamp(fWidthPx * ratio * 1.0, 90, 235);
    var widthDiagPx = clamp(fWidthPx * 0.42, 25, 75);

    var tf = empty ? 0.15 : clamp(uT / uW * 1.2, 0.08, 0.35);
    var sH = clamp(fWidthPx * tf, 10, 42);

    // Unit direction vectors (fixed isometric angles, confirmed against reference photo)
    var dirLen   = [0.98776, -0.15596];  // front edge, gently rising to the right
    var dirWidth = [-0.35120, -0.93660]; // short edge, rising steeply to the left

    var Ax = 125, Ay = 108; // pivot: front-left top-face corner
    var A = [Ax, Ay];
    var B = [Ax + dirWidth[0] * widthDiagPx, Ay + dirWidth[1] * widthDiagPx]; // back-left
    var D = [Ax + dirLen[0] * fLenPx,        Ay + dirLen[1] * fLenPx];        // front-right
    var C = [B[0] + dirLen[0] * fLenPx,      B[1] + dirLen[1] * fLenPx];      // back-right

    // Aliases matching prior face-drawing structure
    var bl = A, tl = B, tr = C, br = D;
    var fbl = [A[0], A[1] + sH];
    var fbr = [D[0], D[1] + sH];
    var rbr = [C[0], C[1] + sH];
    var rbl = [B[0], B[1] + sH];

    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }
    function topPt(u, v) {
      return [tl[0] + u * (tr[0] - tl[0]) + v * (bl[0] - tl[0]),
              tl[1] + u * (tr[1] - tl[1]) + v * (bl[1] - tl[1])];
    }

    /* ── Face colors ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', strokeC = '#2A2A28';
    var fabricLum = 200; // luminance estimate for piping contrast
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        fabricLum = (sc[0] + sc[1] + sc[2]) / 3;
        if (fabricLum > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="cc-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Straps (from BOTTOM corners, drawn behind cushion) ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 16;

      // Helper: draw a pair of ties from a bottom corner
      function drawTies(cx_, cy_, dirX, dirY) {
        // Two diverging ribbons hanging down
        var spread = 4;
        h += '<path d="M' + f(cx_ - spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ - spread + dirX * 3) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ - spread * 1.5 + dirX * 5) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(cx_ + spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ + spread + dirX * 2) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ + spread * 1.5 + dirX * 4) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }

      // 2 straps: front-left bottom + front-right bottom
      drawTies(fbl[0], fbl[1], -1, 1);
      drawTies(fbr[0], fbr[1], 1, 1);

      if (state.straps === 'four') {
        // back-left bottom + back-right bottom
        drawTies(rbl[0], rbl[1], -1, 1);
        drawTies(rbr[0], rbr[1], 1, 1);
      }
    }

    /* ── Main faces ── */
    h += '<polygon points="' + [tl, tr, br, bl].map(pt).join(' ') + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [bl, br, fbr, fbl].map(pt).join(' ') + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    h += '<polygon points="' + [bl, tl, rbl, fbl].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Tufted dots ── */
    if ((state.shape === 'tufted_rectangle' || state.shape === 'france_tufted') && !empty) {
      var tRows = Math.max(2, Math.min(4, Math.round(uL / 10)));
      var tCols = Math.max(2, Math.min(4, Math.round(uW / 8)));
      var dotFill = darken(topC, 0.12);
      for (var row = 0; row < tRows; row++) {
        for (var col = 0; col < tCols; col++) {
          var du = (col + 1) / (tCols + 1);
          var dv = (row + 1) / (tRows + 1);
          var dp = topPt(du, dv);
          h += '<circle cx="' + f(dp[0]) + '" cy="' + f(dp[1]) + '" r="2" fill="' + dotFill + '" stroke="' + strokeC + '" stroke-width="0.5" opacity="0.5"/>';
          h += '<circle cx="' + f(dp[0] - 0.4) + '" cy="' + f(dp[1] - 0.4) + '" r="0.7" fill="#fff" opacity="0.35"/>';
        }
      }
    }

    /* ── Piping (thin contrast cord, not a filled band) ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8';
      var pw = 2.2;

      // TOP ring: along top face perimeter (where top meets sides)
      h += '<polyline points="' + [bl, tl, tr, br, bl].map(pt).join(' ')
        + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';

      if (state.piping === 'double') {
        // BOTTOM ring: only the two visible bottom edges — no dashed hidden lines (kept clean)
        h += '<polyline points="' + [rbl, fbl, fbr].map(pt).join(' ')
          + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip (thin strip hugging the actual sloped bottom edge) ── */
    if (state.antislip && state.antislip !== 'none' && !empty) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      var padOffset = 1, padThick = 4, padInset = 5;
      var normalDir = [-dirLen[1], dirLen[0]]; // points away/below the front edge

      function stripQuad(t0, t1) {
        var edgeLen = Math.sqrt(Math.pow(fbr[0] - fbl[0], 2) + Math.pow(fbr[1] - fbl[1], 2));
        var q1 = [fbl[0] + (fbr[0] - fbl[0]) * (t0 / edgeLen), fbl[1] + (fbr[1] - fbl[1]) * (t0 / edgeLen)];
        var q2 = [fbl[0] + (fbr[0] - fbl[0]) * (t1 / edgeLen), fbl[1] + (fbr[1] - fbl[1]) * (t1 / edgeLen)];
        var q3 = [q2[0] + normalDir[0] * (padOffset + padThick), q2[1] + normalDir[1] * (padOffset + padThick)];
        var q4 = [q1[0] + normalDir[0] * (padOffset + padThick), q1[1] + normalDir[1] * (padOffset + padThick)];
        var q1o = [q1[0] + normalDir[0] * padOffset, q1[1] + normalDir[1] * padOffset];
        var q2o = [q2[0] + normalDir[0] * padOffset, q2[1] + normalDir[1] * padOffset];
        return [q1o, q2o, q3, q4];
      }

      var edgeLenPx = Math.sqrt(Math.pow(fbr[0] - fbl[0], 2) + Math.pow(fbr[1] - fbl[1], 2));

      if (state.antislip === 'velcro') {
        var strips = 5, gapPx = edgeLenPx / strips;
        for (var vi = 0; vi < strips; vi++) {
          var t0 = vi * gapPx + 3, t1 = (vi + 1) * gapPx - 3;
          if (t1 <= t0) continue;
          var quad = stripQuad(t0, t1);
          h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
        }
      } else {
        var quad = stripQuad(padInset, edgeLenPx - padInset);
        h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
      }
    }

    /* ── Dimension labels — hugging the actual edges ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f';
    var ls = empty ? 12 : 11;
    var hug = 20;
    var lblW = empty ? '' : ' — ' + W + '″';
    var lblL = empty ? '' : ' — ' + L + '″';
    var lblT = empty ? '' : ' — ' + T + '″';

    var centroid = [(A[0] + B[0] + C[0] + D[0]) / 4, (A[1] + B[1] + C[1] + D[1]) / 4];
    function outward(p1, p2) {
      var mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
      var vx = mid[0] - centroid[0], vy = mid[1] - centroid[1];
      var len = Math.sqrt(vx * vx + vy * vy) || 1;
      return [vx / len, vy / len];
    }

    var oL = outward(A, D);
    var la1 = [fbl[0] + oL[0] * hug, fbl[1] + oL[1] * hug];
    var la2 = [fbr[0] + oL[0] * hug, fbr[1] + oL[1] * hug];
    h += '<line x1="' + f(fbl[0]) + '" y1="' + f(fbl[1]) + '" x2="' + f(la1[0]) + '" y2="' + f(la1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(fbr[0]) + '" y1="' + f(fbr[1]) + '" x2="' + f(la2[0]) + '" y2="' + f(la2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(la1[0]) + '" y1="' + f(la1[1]) + '" x2="' + f(la2[0]) + '" y2="' + f(la2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#cc-arr)" marker-end="url(#cc-arr)"/>';
    h += '<text x="' + f((la1[0] + la2[0]) / 2) + '" y="' + f((la1[1] + la2[1]) / 2 + 16) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Length' + lblL + '</text>';

    var oW = outward(A, B);
    var wa1 = [fbl[0] + oW[0] * hug, fbl[1] + oW[1] * hug];
    var wa2 = [rbl[0] + oW[0] * hug, rbl[1] + oW[1] * hug];
    h += '<line x1="' + f(fbl[0]) + '" y1="' + f(fbl[1]) + '" x2="' + f(wa1[0]) + '" y2="' + f(wa1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(rbl[0]) + '" y1="' + f(rbl[1]) + '" x2="' + f(wa2[0]) + '" y2="' + f(wa2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(wa1[0]) + '" y1="' + f(wa1[1]) + '" x2="' + f(wa2[0]) + '" y2="' + f(wa2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#cc-arr)" marker-end="url(#cc-arr)"/>';
    h += '<text x="' + f((wa1[0] + wa2[0]) / 2 - 8) + '" y="' + f((wa1[1] + wa2[1]) / 2 + 4) + '" text-anchor="end" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Width' + lblW + '</text>';

    var thx = fbr[0] + hug;
    h += '<line x1="' + f(D[0]) + '" y1="' + f(D[1]) + '" x2="' + f(thx) + '" y2="' + f(D[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(fbr[0]) + '" y1="' + f(fbr[1]) + '" x2="' + f(thx) + '" y2="' + f(fbr[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(D[1] + 2) + '" x2="' + f(thx) + '" y2="' + f(fbr[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#cc-arr)" marker-end="url(#cc-arr)"/>';
    h += '<text x="' + f(thx + 5) + '" y="' + f((D[1] + fbr[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">T' + lblT + '</text>';

    // Empty-state hint
    if (empty) {
      h += '<text x="240" y="168" text-anchor="middle" font-size="10.5" font-family="Poppins,sans-serif" fill="#8A8A85">Enter your existing cushion dimensions to see the cover</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.L && state.W && state.T) parts.push(state.L + ' × ' + state.W + ' × ' + state.T + '″');
    if (state.shape) parts.push(LABELS[state.shape] || state.shape);
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your cover below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var currentFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (currentFabric && currentFabric !== state.fabric) {
      state.fabric = currentFabric;
    }
    state.L = parseFloat(els.inputL.value) || 0;
    state.W = parseFloat(els.inputW.value) || 0;
    state.T = parseFloat(els.inputT.value) || 0;
    state.extraCovers = els.extraCovers ? els.extraCovers.value : '0';
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    ['inputL', 'inputW', 'inputT'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    drawCushion();
    updateSummary();

    var hasDim = state.L >= 6 && state.W >= 6 && state.T >= 1;
    var ec = parseInt(state.extraCovers) || 0;
    var base = hasDim ? calcBasePrice(state.L, state.W, state.T, ec) : null;
    var addonTotal = getAddonTotal();

    if (base !== null) {
      var finalPrice = Math.round(base + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Base $' + Math.round(base)];
      if (ADDON.shape[state.shape])             parts.push('Shape +$' + ADDON.shape[state.shape]);
      if (ADDON.piping[state.piping])           parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])           parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])           parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip])       parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ADDON.extraCovers[ec])                parts.push('Covers +$' + ADDON.extraCovers[ec]);
      if (ADDON.throwPillow[state.throwPillow]) parts.push('Throw Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Pillow Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    // Update BOTH buttons (sticky + bottom)
    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        var fp = Math.round(base + addonTotal);
        btn.textContent = 'Add to order — $' + fp + ' →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    // Update note below bottom button
    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!state.shape) missing.push('shape');
        if (!hasDim) missing.push('dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: { price: base !== null ? Math.round(base + addonTotal) : null, state: state }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var base = calcBasePrice(state.L, state.W, state.T, ec);
    var finalPrice = Math.round(base + getAddonTotal());
    var props = {
      'Shape': LABELS[state.shape] || state.shape,
      'Length': state.L + '"', 'Width': state.W + '"', 'Thickness': state.T + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s)';
    if (state.throwPillow !== 'none') props['Throw Pillow'] = state.throwPillow.replace('pcs_', ' pcs ');
    if (state.pillowCovers !== 'none') props['Pillow Covers'] = state.pillowCovers.replace('pcs_', ' pcs ');
    if (state.armrestPillow !== 'none') props['Armrest Pillow'] = state.armrestPillow.replace('_', ' ');
    if (state.zipperPlacement) {
      var ZIP_PLACEMENT_LABELS = { A: 'A — Left side', B: 'B — Back edge', C: 'C — Right side', D: 'D — Front edge (facing you)' };
      props['Zipper Placement'] = ZIP_PLACEMENT_LABELS[state.zipperPlacement] || state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = sourceProductName();

    var apiPayload = {
      formulaType: 'cover',
      length: state.L, width: state.W, thickness: state.T,
      shape: state.shape, piping: state.piping, pipingColor: state.pipingColor, straps: state.straps,
      zipper: state.zipper, antislip: state.antislip,
      extraCovers: ec, throwPillow: state.throwPillow,
      pillowCovers: state.pillowCovers, armrestPillow: state.armrestPillow
    };

    return { properties: props, price: finalPrice, apiPayload: apiPayload };
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.inputL       = document.getElementById('cc-dim-l');
    els.inputW       = document.getElementById('cc-dim-w');
    els.inputT       = document.getElementById('cc-dim-t');
    els.extraCovers    = document.getElementById('cc-extra-covers');
    els.throwPillow    = document.getElementById('cc-throw-pillow');
    els.pillowCovers   = document.getElementById('cc-pillow-covers');
    els.armrestPillow  = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    els.inputL.addEventListener('input', recalc);
    els.inputW.addEventListener('input', recalc);
    els.inputT.addEventListener('input', recalc);
    if (els.extraCovers) els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    // Both buttons trigger add-to-cart
    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    // v1 cover: rectangular only — auto-select shape and hide the shape step.
    state.shape = 'rectangular';
    var shapeSection = document.getElementById('cc-shape-section');
    if (shapeSection) shapeSection.style.display = 'none';

    // Cover-only clarification. Injected from JS (cover.js loads on cover pages
    // only) so the SHARED block.liquid is untouched — other configurators never
    // show this. Sits just under the dimensions hint, where customers look.
    if (!document.getElementById('cc-cover-notice')) {
      var notice = document.createElement('div');
      notice.id = 'cc-cover-notice';
      notice.setAttribute('style',
        'margin:2px 16px 10px;padding:9px 12px;background:#F0EAD8;'
        + 'border:1px solid #D8C9A0;border-left:3px solid #6B5A2A;'
        + 'color:#4A4A47;font-size:11.5px;line-height:1.45;');
      notice.innerHTML = '<strong style="color:#2A2A28">Cover only — no foam insert.</strong> '
        + 'You’re ordering the replacement fabric cover for a cushion you already have. '
        + 'Enter your existing cushion’s size below.';
      var hint = wrap.querySelector('.cc-sp-hint');
      if (hint && hint.parentNode) {
        hint.parentNode.insertBefore(notice, hint.nextSibling);
      } else {
        wrap.insertBefore(notice, wrap.firstChild);
      }
    }

    drawCushion();
    recalc();
  }

  window.CoverConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();